<template>
	<Transition>
		<div
			ref="panel"
			data-testid="request-coupling-panel"
			:style="{ width: `${panelWidth}px` }"
			:class="{ enlarged: expanded }"
			v-show="
				selectedDocument ||
				useBreakpoints(breakpointsTailwind).greaterOrEqual('xl').value
			"
			class="document-sidebar absolute right-0 h-full max-w-full border-l bg-white opacity-100 drop-shadow-lg xl:relative xl:drop-shadow-none xl:block grow-0 shrink-0"
		>
			<div
				v-if="selectedDocument"
				data-testid="document-resize-handle"
				class="resize-handle"
				aria-hidden="true"
				@pointerdown.stop.prevent="startResize"
				@pointermove="resize"
				@pointerup="stopResize"
				@pointercancel="stopResize"
				@lostpointercapture="stopResize"
			/>
			<div class="h-full overflow-y-auto">
				<LoanApplicationDocumentsDetail
					v-if="selectedDocument"
					:selected-document
					:expanded="expanded"
					@toggle-expand="toggleExpand"
					@disconnect-document="disconnectDocument()"
					@connect-document="(doc) => connectDocument(doc)"
					@send-document="sendDocument()"
					@close="close"
				/>
				<div v-else class="flex justify-center h-full">
					<span class="mt-20 text-gray-500">Selecteer een document</span>
				</div>
			</div>
		</div>
	</Transition>
</template>

<script lang="ts" setup>
import type {
	DossierDocument,
	HdnRequestedDocument,
} from "@/composables/useLoanApplicationSortedDocuments";
import { isHdnNativeDocumentTooLarge } from "@/helpers/hdnDocumentSize";
import { useZero } from "@/composables/useZero";
import { useUserStore } from "@/store/useUserStore";
import { mutators } from "@loanlink-nl/zero";
import {
	useBreakpoints,
	breakpointsTailwind,
	useResizeObserver,
} from "@vueuse/core";
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import LoanApplicationDocumentsDetail from "./LoanApplicationDocumentsDetail.vue";

const emit = defineEmits<{
	(e: "close"): void;
}>();
const props = defineProps<{ selectedDocument?: HdnRequestedDocument | null }>();
const panel = ref<HTMLElement>();
const page = ref<HTMLElement>();
const availableWidth = ref(384);
const requestedWidth = ref(384);
const minWidth = computed(() => Math.min(384, availableWidth.value));
const maxWidth = computed(() => Math.min(960, availableWidth.value));
const panelWidth = computed(() =>
	Math.max(minWidth.value, Math.min(requestedWidth.value, maxWidth.value)),
);
const expanded = computed(() => panelWidth.value > minWidth.value);
let drag:
	| {
			pointerId: number;
			startX: number;
			startWidth: number;
			target: HTMLElement;
	  }
	| undefined;
onMounted(() => {
	page.value = panel.value?.parentElement ?? undefined;
	availableWidth.value = page.value?.clientWidth ?? 384;
});
useResizeObserver(page, () => {
	availableWidth.value = page.value?.clientWidth ?? 384;
});
function toggleExpand() {
	stopResize();
	requestedWidth.value = expanded.value ? minWidth.value : maxWidth.value;
}
function startResize(event: PointerEvent) {
	if (event.button !== 0 || !event.isPrimary) return;
	stopResize();
	const target = event.currentTarget as HTMLElement;
	drag = {
		pointerId: event.pointerId,
		startX: event.clientX,
		startWidth: panelWidth.value,
		target,
	};
	target.setPointerCapture(event.pointerId);
}
function resize(event: PointerEvent) {
	if (!drag || event.pointerId !== drag.pointerId) return;
	requestedWidth.value = Math.max(
		minWidth.value,
		Math.min(maxWidth.value, drag.startWidth + drag.startX - event.clientX),
	);
}
function stopResize() {
	const previous = drag;
	drag = undefined;
	if (previous?.target.hasPointerCapture(previous.pointerId))
		previous.target.releasePointerCapture(previous.pointerId);
}
function close() {
	stopResize();
	requestedWidth.value = 384;
	emit("close");
}
watch(
	() => props.selectedDocument,
	(selected) => {
		if (!selected) {
			stopResize();
			requestedWidth.value = 384;
		}
	},
);
onBeforeUnmount(stopResize);

function connectDocument(doc: DossierDocument) {
	const user = useUserStore().user;
	if (!props.selectedDocument || !user) {
		return;
	}

	if (isHdnNativeDocumentTooLarge(doc.byteSize, doc.filename)) {
		return;
	}

	const z = useZero();
	z.value.mutate(
		mutators.hdnRequestedDocument.connect({
			actionId: crypto.randomUUID(),
			documentId: doc.id,
			id: props.selectedDocument.id,
		}),
	);
}

/**
 * Sends the connected document to HDN
 *
 * TODO: show confirmation to the user before executing this action
 */
function sendDocument() {
	const user = useUserStore().user;
	if (!props.selectedDocument || !user) {
		return;
	}

	if (!props.selectedDocument.linkedDocumentAction?.documentId) {
		return;
	}

	const z = useZero();
	z.value.mutate(
		mutators.hdnRequestedDocument.send({
			actionId: crypto.randomUUID(),
			id: props.selectedDocument.id,
			documentId: props.selectedDocument.linkedDocumentAction.documentId,
		}),
	);
}

/**
 * Disconnects the document from the requested document
 */
function disconnectDocument() {
	const user = useUserStore().user;
	if (!props.selectedDocument || !user) {
		return;
	}

	const z = useZero();
	z.value.mutate(
		mutators.hdnRequestedDocument.disconnect({
			actionId: crypto.randomUUID(),
			id: props.selectedDocument.id,
		}),
	);
}
</script>

<style lang="scss" scoped>
.document-sidebar {
	z-index: 10;
}
.document-sidebar.enlarged {
	position: absolute;
	box-shadow: -4px 0 16px #0002;
}
.resize-handle {
	position: absolute;
	left: 0;
	top: 0;
	bottom: 0;
	width: 8px;
	cursor: col-resize;
	touch-action: none;
	z-index: 20;
}
.resize-handle:hover {
	background: var(--background-secondary);
}
.v-enter-active,
.v-leave-active {
	transition: all 0.15s ease;
}

.v-enter-from,
.v-leave-to {
	opacity: 0;
}

@media (prefers-reduced-motion: no-preference) {
	.v-enter-from,
	.v-leave-to {
		opacity: 1;
		transform: translateX(100%);
	}
}
</style>
